import notEmpty from "@/functions/notEmpty";
import type { IEventActionFilter } from "@/graphql";
import { Filter, FilterEntity } from "./FilterTypes";

import * as FilterClasses from "./filterImplementations";

export type SerializedFilter = {
  className: string;
  values: any;
  type: FilterEntity;
};

const legacyActionStatus = ["PLAN", "DO", "CHECK", "ACT"] as const;
const legacyBoardOrigin = ["CREATED", "DELEGATED", "ESCALATED"] as const;

function normalizeLegacyValues(filter: SerializedFilter): Record<string, unknown> | null {
  const values =
    filter.values != null && typeof filter.values === "object"
      ? (filter.values as Record<string, unknown>)
      : {};

  switch (filter.className) {
    case "CreatorFilter":
      return { ...values, userIds: values.userIds ?? values.creators ?? [] };
    case "FlagsFilter":
      return { ...values, flagIds: values.flagIds ?? values.flags ?? [] };
    case "StatusFilter":
      return {
        ...values,
        status: Array.isArray(values.status)
          ? values.status.map((status) =>
              typeof status === "number" ? legacyActionStatus[status - 1] : status,
            )
          : [],
      };
    case "BoardOriginFilter":
      return {
        ...values,
        boardOrigin:
          typeof values.boardOrigin === "number"
            ? legacyBoardOrigin[values.boardOrigin]
            : values.boardOrigin,
      };
    case "IDFilter":
      // Legacy type names cannot be mapped reliably to configurable type IDs.
      return { ...values, typeId: typeof values.typeId === "number" ? values.typeId : undefined };
    case "CustomAttributeFilter":
      // Legacy custom attributes were name-based; the new board requires a configured field ID.
      return typeof values.fieldId === "number" ? values : null;
    default:
      return values;
  }
}

export default class FilterSerializer {
  static serialize({
    eventFilters,
    actionFilters,
  }: {
    eventFilters: Filter[];
    actionFilters: Filter[];
  }) {
    const serializeFilters = (filters: Filter[], type: FilterEntity): IEventActionFilter[] =>
      filters
        .map((filter) => {
          // We intentionally do not use filter.constructor.name here as it sometimes provides weird values. Instead we look if the provided filter is an instance of any known filters.
          const className = Object.entries(FilterClasses).find(
            ([, classType]) => filter instanceof classType,
          )?.[0];

          if (!className) {
            return null;
          }

          return {
            className,
            values: typeof filter.serialize === "function" ? filter.serialize() : filter,
            type,
          } as SerializedFilter;
        })
        .filter(notEmpty);

    return [
      ...serializeFilters(eventFilters, FilterEntity.EVENT),
      ...serializeFilters(actionFilters, FilterEntity.ACTION),
    ];
  }

  static deserialize(allFilters: SerializedFilter[]) {
    const deserializeFilters = (filters: SerializedFilter[]) =>
      filters
        .map((filter) => {
          const FilterClass = FilterClasses[
            filter.className as keyof typeof FilterClasses
          ] as unknown as typeof Filter | undefined;
          const values = normalizeLegacyValues(filter);
          if (FilterClass == null || values == null) {
            return null;
          }
          if (FilterClass.deserialize) {
            return FilterClass.deserialize(values);
          }
          const filterObject = new FilterClass();
          Object.assign(filterObject, values);
          return filterObject;
        })
        .filter(notEmpty);

    return {
      eventFilters: deserializeFilters(
        allFilters.filter(({ type }) => type === FilterEntity.EVENT),
      ),
      actionFilters: deserializeFilters(
        allFilters.filter(({ type }) => type === FilterEntity.ACTION),
      ),
    };
  }
}
